import { Injectable } from '@angular/core';
import { PROPERTY_TAX_RATES, PropertyTaxRate } from '../constants/property-tax-rates';
import { ACQUISITION_TAX_RATES, AcquisitionTaxRate } from '../constants/acquisition-tax-rates';
import { ACQUISITION_TAX_RATES_BY_CITY, AcquisitionTaxRateByCity } from '../constants/acquisition-tax-rates-by-city';

/**
 * Service to manage property tax rate data and acquisition tax rate data
 * 
 * Currently uses local constants but can be easily extended to use a backend API/database:
 * 
 * TODO: When migrating to database, replace the local data with HTTP calls:
 * - getAll(): return this.http.get<PropertyTaxRate[]>('/api/property-tax-rates')
 * - getByState(): return this.http.get<PropertyTaxRate>(`/api/property-tax-rates/${stateName}`)
 * - getAllAcquisitionTaxRates(): return this.http.get<AcquisitionTaxRate[]>('/api/acquisition-tax-rates')
 * - getAcquisitionTaxRateByState(): return this.http.get<AcquisitionTaxRate>(`/api/acquisition-tax-rates/${stateName}`)
 * - getAcquisitionTaxRateByCity(): return this.http.get<AcquisitionTaxRateByCity>(`/api/acquisition-tax-rates-by-city/${city}`)
 */
@Injectable({
  providedIn: 'root'
})
export class PropertyTaxRateService {
  
  /**
   * Get all available states with their property tax rates
   */
  getAll(): PropertyTaxRate[] {
    return PROPERTY_TAX_RATES;
  }

  /**
   * Get property tax rate for a specific state
   */
  getByState(stateName: string): PropertyTaxRate | null {
    return PROPERTY_TAX_RATES.find(
      r => r.stateName.toLowerCase() === stateName.toLowerCase()
    ) ?? null;
  }

  /**
   * Get all state names sorted alphabetically
   */
  getStateNames(): string[] {
    return PROPERTY_TAX_RATES.map(r => r.stateName).sort();
  }

  /**
   * Get property tax rate percentage for a state
   */
  getTaxRateByState(stateName: string): number | null {
    return this.getByState(stateName)?.taxRate ?? null;
  }

  /**
   * Get state code by name
   */
  getStateCode(stateName: string): string | null {
    return this.getByState(stateName)?.stateCode ?? null;
  }

  /**
   * Get all available states with their acquisition tax rates
   */
  getAllAcquisitionTaxRates(): AcquisitionTaxRate[] {
    return ACQUISITION_TAX_RATES;
  }

  /**
   * Get acquisition tax rate for a specific state
   */
  getAcquisitionTaxRateByState(stateName: string): AcquisitionTaxRate | null {
    return ACQUISITION_TAX_RATES.find(
      r => r.stateName.toLowerCase() === stateName.toLowerCase()
    ) ?? null;
  }

  /**
   * Get acquisition tax rate percentage for a state
   */
  getAcquisitionTaxPercentageByState(stateName: string): number | null {
    return this.getAcquisitionTaxRateByState(stateName)?.taxRate ?? null;
  }

  /**
   * Get all available cities with their acquisition tax rates
   */
  getAllCitiesWithAcquisitionTaxRates(): AcquisitionTaxRateByCity[] {
    return ACQUISITION_TAX_RATES_BY_CITY;
  }

  /**
   * Get acquisition tax rate for a specific city
   */
  getAcquisitionTaxRateByCity(city: string): AcquisitionTaxRateByCity | null {
    return ACQUISITION_TAX_RATES_BY_CITY.find(
      r => r.city.toLowerCase() === city.toLowerCase()
    ) ?? null;
  }

  /**
   * Get acquisition tax rate percentage for a specific city
   */
  getAcquisitionTaxPercentageByCity(city: string): number | null {
    return this.getAcquisitionTaxRateByCity(city)?.taxRate ?? null;
  }

  /**
   * Get all cities for a specific state, sorted alphabetically
   */
  getCitiesByState(stateName: string): string[] {
    return ACQUISITION_TAX_RATES_BY_CITY
      .filter(r => r.state.toLowerCase() === stateName.toLowerCase())
      .map(r => r.city)
      .sort();
  }

  /**
   * Get all cities with acquisition tax rates, sorted alphabetically
   */
  getAllCities(): string[] {
    return ACQUISITION_TAX_RATES_BY_CITY
      .map(r => r.city)
      .filter((city, index, self) => self.indexOf(city) === index)
      .sort();
  }

  /**
   * Get state (Bundesland) by city name
   */
  getStateByCity(city: string): string | null {
    return ACQUISITION_TAX_RATES_BY_CITY.find(
      r => r.city.toLowerCase() === city.toLowerCase()
    )?.state ?? null;
  }
}
